import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { AskRequest, QueueView } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { useDraggable } from '@dnd-kit/core';
import { RequestCard, RequestCardProps } from '../../Panels/BrowserPanel/components/RequestCard';
import { AssignmentButton } from '../../Panels/ConversationPanel/components/Header/components/AssignmentButton';
import { MobileActionsPanel } from '../MobileActionsPanel';
import { IconMoreV, IconValidateNew, IconWSTodo, IconWSClosed, IconPromote, IconReopen } from 'components/Icons';
import IconButton from '../../Common/IconButton';
import './SwipeableRequestCard.scss';

// Swipe thresholds
const SWIPE_THRESHOLD = 80; // Minimum distance to trigger swipe actions reveal
const SWIPE_VELOCITY_THRESHOLD = 0.3; // pixels per ms for quick swipe
const LONG_PRESS_FOR_DRAG_DURATION = 400; // ms for long press to activate drag mode
const TAP_THRESHOLD = 10; // max movement to still count as a tap

export type SwipeAction = 'assign' | 'resolve' | 'block' | 'unblock' | 'reopen' | 'more';

/** Drag position info passed to parent */
export interface DragPosition {
  x: number;
  y: number;
}

interface SwipeableRequestCardProps extends RequestCardProps {
  /** The full request object (for AssignmentButton and blocker detection) */
  request: AskRequest;
  /** Whether user can manage queue (accountant) */
  canManageQueue?: boolean;
  /** Current active filter view - determines contextual action */
  activeView?: QueueView;
  /** Array of all selected IDs (for multi-drag) */
  selectedIds: (string | number)[];
  /** Whether the card is in selection mode */
  isSelectionMode?: boolean;
  /** Callback when card is tapped (opens details) */
  onTap?: () => void;
  /** Callback when card is long-pressed (enters selection mode) */
  onLongPress?: () => void;
  /** Callback when a swipe action is triggered */
  onSwipeAction?: (action: SwipeAction) => void;
  /** Disable dragging */
  dragDisabled?: boolean;
  /** Callback when drag-to-filter starts (swipe up on selected card) */
  onDragStart?: () => void;
  /** Callback while dragging - reports touch position */
  onDragMove?: (position: DragPosition) => void;
  /** Callback when drag ends - reports final position */
  onDragEnd?: (position: DragPosition) => void;
  /** Callback when drag is cancelled */
  onDragCancel?: () => void;
}

/**
 * SwipeableRequestCard - Mobile request card with swipe gestures
 *
 * Gestures:
 * - Tap: Opens details screen
 * - Long-press: Enters selection mode
 * - Swipe right: Reveals action buttons (Assign, Block/Resolve, More)
 * - Drag (when selected): Can drag to filter pills
 */
export const SwipeableRequestCard: React.FC<SwipeableRequestCardProps> = ({
  id,
  request,
  canManageQueue = false,
  activeView,
  selectedIds,
  isSelected,
  isSelectionMode = false,
  onTap,
  onLongPress,
  onSwipeAction,
  dragDisabled = false,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
  ...cardProps
}) => {
  const prefix = prefixClass('ksq2-swipeable-card');

  // Swipe state
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isActionsRevealed, setIsActionsRevealed] = useState(false);
  const [isQuickReturn, setIsQuickReturn] = useState(false); // Fast return for swipe-right select

  // Drag-to-filter state
  const [isDragToFilterActive, setIsDragToFilterActive] = useState(false);

  // Actions dropdown state
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  // Touch tracking
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTouchRef = useRef<{ x: number; time: number } | null>(null);
  const dragLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const wasScrollingRef = useRef(false); // Track if user was scrolling vertically
  const cardContainerRef = useRef<HTMLDivElement | null>(null); // Ref for native event listeners
  const isDragToFilterActiveRef = useRef(false); // Ref to access in native event listener

  // Keep ref in sync with state for native event listener
  useEffect(() => {
    isDragToFilterActiveRef.current = isDragToFilterActive;
  }, [isDragToFilterActive]);

  // Safety: ensure translateX returns to 0 when not actively dragging or showing actions
  useEffect(() => {
    if (!isDragging && !isActionsRevealed && translateX !== 0) {
      // Small delay to allow quick return animation to start first
      const timer = setTimeout(() => {
        setTranslateX(0);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isDragging, isActionsRevealed, translateX]);

  // Native touchmove listener to prevent scrolling during drag-to-filter
  // React's synthetic events are passive, so we need native listeners with { passive: false }
  useEffect(() => {
    const el = cardContainerRef.current;
    if (!el) return;

    const handleNativeTouchMove = (e: TouchEvent) => {
      if (isDragToFilterActiveRef.current) {
        e.preventDefault();
      }
    };

    el.addEventListener('touchmove', handleNativeTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchmove', handleNativeTouchMove);
    };
  }, []);

  // dnd-kit for drag-to-filter (only when selected)
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging: isDndDragging,
  } = useDraggable({
    id,
    disabled: dragDisabled || !isSelected || !isSelectionMode,
    data: {
      selectedIds: isSelected && selectedIds.length > 0 ? selectedIds : [id],
    },
  });

  const isBlocker = request.priority === 'Blocker';

  // Combined ref for card container (dnd-kit + our own ref for native events)
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      cardContainerRef.current = node;
    },
    [setNodeRef]
  );

  // Determine contextual action based on active view and blocker state
  const contextualAction = useMemo((): { action: SwipeAction; label: string; icon: React.ReactNode } => {
    // For accountants viewing a blocker, show Unblock option
    if (canManageQueue && isBlocker) {
      return { action: 'unblock', label: 'Unblock', icon: <IconPromote /> };
    }

    switch (activeView) {
      case 'Blockers':
        return { action: 'resolve', label: 'Resolve', icon: <IconValidateNew /> };
      case 'Completed':
        return { action: 'reopen', label: 'Reopen', icon: <IconReopen /> };
      default:
        // Me, My Team, Client - show Resolve
        return { action: 'resolve', label: 'Resolve', icon: <IconValidateNew /> };
    }
  }, [activeView, canManageQueue, isBlocker]);

  // Clear drag long press timer
  const clearDragLongPressTimer = useCallback(() => {
    if (dragLongPressTimerRef.current) {
      clearTimeout(dragLongPressTimerRef.current);
      dragLongPressTimerRef.current = null;
    }
  }, []);

  // Reset swipe state
  const resetSwipe = useCallback((quick = false) => {
    if (quick) {
      setIsQuickReturn(true);
      // Reset quick return flag after animation
      setTimeout(() => setIsQuickReturn(false), 100);
    }
    setTranslateX(0);
    setIsActionsRevealed(false);
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // If actions are revealed and touch is on the card, close them
      if (isActionsRevealed) {
        resetSwipe();
        return;
      }

      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      lastTouchRef.current = { x: touch.clientX, time: Date.now() };
      wasScrollingRef.current = false; // Reset scroll tracking

      // Long press timer for drag-to-filter (only for selected cards)
      if (isSelected && !dragDisabled) {
        dragLongPressTimerRef.current = setTimeout(() => {
          setIsDragToFilterActive(true);
          isDragToFilterActiveRef.current = true; // Set ref immediately for native event listener
          onDragStart?.();
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }, LONG_PRESS_FOR_DRAG_DURATION);
      }
    },
    [isActionsRevealed, resetSwipe, isSelected, dragDisabled, onDragStart]
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;

      // If already in drag-to-filter mode (activated by long press), report position and prevent scroll
      if (isDragToFilterActive) {
        e.preventDefault();
        onDragMove?.({ x: touch.clientX, y: touch.clientY });
        return;
      }

      // If moved beyond tap threshold, cancel drag long press timer and handle swipe/scroll
      if (Math.abs(deltaX) > TAP_THRESHOLD || Math.abs(deltaY) > TAP_THRESHOLD) {
        clearDragLongPressTimer();

        // If vertical movement is greater, it's scrolling
        if (Math.abs(deltaY) > Math.abs(deltaX) && !isDragging) {
          wasScrollingRef.current = true;
          return;
        }

        // Swipe left (negative deltaX) - reveal actions
        if (deltaX < 0) {
          setIsDragging(true);
          // Limit the swipe distance with resistance
          const maxSwipe = 180;
          const resistance = 0.5;
          const absDelta = Math.abs(deltaX);
          const resistedDelta = absDelta > maxSwipe ? maxSwipe + (absDelta - maxSwipe) * resistance : absDelta;

          requestAnimationFrame(() => {
            setTranslateX(-resistedDelta);
          });
        }
        // Swipe right (positive deltaX) - select card
        else if (deltaX > 0) {
          setIsDragging(true);
          // Smaller max swipe for selection gesture
          const maxSwipe = 60;
          const resistance = 0.3;
          const resistedDelta = deltaX > maxSwipe ? maxSwipe + (deltaX - maxSwipe) * resistance : deltaX;

          requestAnimationFrame(() => {
            setTranslateX(resistedDelta);
          });
        }
      }

      // Track for velocity calculation
      lastTouchRef.current = { x: touch.clientX, time: Date.now() };
    },
    [isDragging, isDragToFilterActive, clearDragLongPressTimer, onDragMove]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      clearDragLongPressTimer();

      // Safety: always clean up refs at the end
      const cleanup = () => {
        setIsDragging(false);
        setIsDragToFilterActive(false);
        isDragToFilterActiveRef.current = false; // Reset ref for native event listener
        touchStartRef.current = null;
        lastTouchRef.current = null;
        wasScrollingRef.current = false;
      };

      // Handle drag-to-filter end
      if (isDragToFilterActive) {
        const touch = e.changedTouches[0];
        if (touch) {
          onDragEnd?.({ x: touch.clientX, y: touch.clientY });
        }
        cleanup();
        return;
      }

      if (!touchStartRef.current) {
        cleanup();
        resetSwipe();
        return;
      }

      const startPos = touchStartRef.current;
      const lastPos = lastTouchRef.current;

      // Calculate velocity
      let velocity = 0;
      if (lastPos && lastPos.time > startPos.time) {
        velocity = (lastPos.x - startPos.x) / (lastPos.time - startPos.time);
      }

      // Determine action based on swipe distance and velocity
      if (isDragging) {
        // Swipe left (negative translateX) - reveal actions
        if (translateX < 0 && (Math.abs(translateX) > SWIPE_THRESHOLD || velocity < -SWIPE_VELOCITY_THRESHOLD)) {
          // Reveal actions
          setTranslateX(-180);
          setIsActionsRevealed(true);
        }
        // Swipe right (positive translateX) - select card
        else if (translateX > 0 && (translateX > SWIPE_THRESHOLD / 2 || velocity > SWIPE_VELOCITY_THRESHOLD)) {
          // Trigger selection
          onLongPress?.();
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
          resetSwipe(true); // Quick return
        } else {
          // Snap back for any incomplete swipe
          // Use quick return for right swipes that didn't complete
          resetSwipe(translateX > 0);
        }
      } else if (!wasScrollingRef.current) {
        // Only trigger tap if user wasn't scrolling
        onTap?.();
      }

      cleanup();
    },
    [isDragging, isDragToFilterActive, translateX, onTap, onLongPress, onDragEnd, clearDragLongPressTimer, resetSwipe]
  );

  // Handle touch cancel (interrupted gesture)
  const handleTouchCancel = useCallback(() => {
    clearDragLongPressTimer();

    // Handle drag-to-filter cancel
    if (isDragToFilterActive) {
      onDragCancel?.();
    }

    setIsDragging(false);
    setIsDragToFilterActive(false);
    isDragToFilterActiveRef.current = false; // Reset ref for native event listener
    resetSwipe();
    touchStartRef.current = null;
    lastTouchRef.current = null;
    wasScrollingRef.current = false;
  }, [clearDragLongPressTimer, resetSwipe, isDragToFilterActive, onDragCancel]);

  // Handle action button clicks
  const handleContextualAction = useCallback(() => {
    onSwipeAction?.(contextualAction.action);
    resetSwipe();
  }, [onSwipeAction, contextualAction.action, resetSwipe]);

  const handleMore = useCallback(() => {
    setIsActionsOpen(true);
    // Don't reset swipe - keep actions visible while dropdown is open
  }, []);

  // Close dropdown and reset swipe when dropdown closes
  const handleActionsOpenChange = useCallback(
    (open: boolean) => {
      setIsActionsOpen(open);
      if (!open) {
        resetSwipe();
      }
    },
    [resetSwipe]
  );

  // Close actions when clicking outside
  const handleOverlayClick = useCallback(() => {
    resetSwipe();
  }, [resetSwipe]);

  const cardStyle: React.CSSProperties = {
    transform: `translateX(${translateX}px)`,
    transition: isDragging ? 'none' : isQuickReturn ? 'transform 0.1s ease-out' : 'transform 0.25s ease-out',
  };

  const dndStyle: React.CSSProperties = {
    opacity: isDndDragging ? 0.5 : 1,
    cursor: isSelectionMode && isSelected ? 'grab' : 'default',
  };

  return (
    <div
      className={classNames(prefix(), {
        [prefix('--actions-revealed')]: isActionsRevealed,
        [prefix('--swiping-left')]: isDragging && translateX < 0,
        [prefix('--swiping-right')]: isDragging && translateX > 0,
        [prefix('--selected')]: isSelected,
        [prefix('--blocker')]: isBlocker,
        [prefix('--selection-mode')]: isSelectionMode,
        [prefix('--dnd-dragging')]: isDndDragging,
        [prefix('--drag-to-filter')]: isDragToFilterActive,
      })}
    >
      {/* Overlay to close actions when tapping outside */}
      {isActionsRevealed && <div className={prefix('__overlay')} onClick={handleOverlayClick} />}

      {/* Selection indicator (revealed on swipe left/right gesture) */}
      <div className={prefix('__selection-indicator')}>{isSelected ? <IconWSClosed /> : <IconWSTodo />}</div>

      {/* Action buttons (revealed on swipe left) */}
      <div className={prefix('__actions')}>
        <IconButton
          icon={<IconMoreV />}
          onClick={handleMore}
          className={classNames(prefix('__action'), prefix('__action--more'))}
        />
        <IconButton
          icon={contextualAction.icon}
          onClick={handleContextualAction}
          className={classNames(prefix('__action'), prefix('__action--contextual'))}
        />
        <div className={classNames(prefix('__action'), prefix('__action--assign'))}>
          <AssignmentButton request={request} canManageQueue={canManageQueue} mobile />
        </div>
      </div>

      {/* Card container with swipe transform */}
      <div
        ref={combinedRef}
        className={prefix('__card-container')}
        style={{ ...cardStyle, ...dndStyle }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        {...(isSelectionMode && isSelected ? { ...listeners, ...attributes } : {})}
      >
        <RequestCard id={id} isSelected={isSelected} {...cardProps} />
      </div>

      {/* Actions Panel (slide-up) */}
      <MobileActionsPanel
        request={request}
        isOpen={isActionsOpen}
        onClose={() => handleActionsOpenChange(false)}
        canManageQueue={canManageQueue}
        allowResolveDialog={selectedIds.length === 1}
        showResolveAction={contextualAction.action !== 'resolve'}
      />
    </div>
  );
};

export default SwipeableRequestCard;
